/**
 * Smart search utilities for Arabic and English.
 * Handles Arabic similar-letter normalization (ة/ه, ى/ي, ا/أ) and typo tolerance via Fuse.js.
 */

import Fuse from 'fuse.js';

/**
 * Normalizes text for search: Arabic similar letters + trim + lowercase.
 * @param {string} text - Raw text
 * @returns {string} Normalized text
 */
export function normalizeForSearch(text) {
  if (text == null || typeof text !== 'string') return '';
  let t = text.trim();
  if (!t) return '';
  // Arabic similar-letter normalization
  t = t.replace(/ة/g, 'ه').replace(/ى/g, 'ي').replace(/أ/g, 'ا').replace(/إ/g, 'ا').replace(/آ/g, 'ا').replace(/ٱ/g, 'ا');
  return t.toLowerCase();
}

/**
 * Returns true if normalized query appears in any of the given item fields.
 * @param {object} item - Item to check
 * @param {string} query - Search query
 * @param {string[]} fields - Field names to search in
 * @returns {boolean}
 */
export function matchesSearch(item, query, fields) {
  const qNorm = normalizeForSearch(query);
  if (!qNorm) return true;
  for (const field of fields) {
    const val = item[field];
    if (val != null && normalizeForSearch(String(val)).includes(qNorm)) return true;
  }
  return false;
}

/**
 * Creates a fuzzy search function using Fuse.js.
 * Uses normalized fields so Arabic similar letters and typos both work.
 * @param {object[]} items - Array of items to search
 * @param {object} options - { keys: string[], threshold?: number, ignoreLocation?: boolean }
 * @returns {function(string): object[]} Search function that returns matching items
 */
export function createFuzzySearch(items, options = {}) {
  const { keys = ['name', 'sku'], threshold = 0.4, ignoreLocation = true } = options;
  const normKeys = keys.map((k) => `__norm_${k}`);
  const itemsWithNorm = items.map((item) => {
    const out = { ...item };
    for (const k of keys) {
      const val = item[k];
      out[`__norm_${k}`] = normalizeForSearch(val != null ? String(val) : '');
    }
    return out;
  });
  const fuse = new Fuse(itemsWithNorm, {
    keys: normKeys,
    threshold,
    ignoreLocation,
    includeScore: false,
  });
  return (query) => {
    const q = normalizeForSearch(query);
    if (!q) return items;
    const results = fuse.search(q);
    return results.map((r) => {
      const item = r.item;
      const clean = { ...item };
      for (const k of normKeys) delete clean[k];
      return clean;
    });
  };
}
